import { clamp, preventDefault, isMobileBreakpoint } from './utilities.js';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.3;
const DOUBLE_TAP_ZOOM = 2.5;
const MINIMAP_SIZE = 90;
const MINIMAP_MARGIN = 12;

export class DragZoomWrapper extends HTMLElement {
  #controller = new AbortController();
  #scale = MIN_ZOOM;
  #initialDistance = 0;
  #startScale = MIN_ZOOM;
  #translate = { x: 0, y: 0 };
  #startPosition = { x: 0, y: 0 };
  #startTranslate = { x: 0, y: 0 };
  #isDragging = false;
  #hasDragged = false;
  #initialized = false;
  #animationFrame = null;
  #minimapEl = null;
  #minimapViewport = null;
  #minimapCanvas = null;
  #lastTapTime = 0;
  #isMouseDragging = false;

  get #image() {
    return this.querySelector('img');
  }

  connectedCallback() {
    if (!this.#image) return;
    this.#initResizeListener();
    this.#initEventListeners();

    if (this.#image.complete && this.#image.naturalWidth) {
      this.#updateTransform();
    } else {
      this.#image.addEventListener('load', () => this.#updateTransform(), { once: true });
    }
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    // Touch
    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);

    // Mouse
    this.addEventListener('mousedown', this.#handleMouseDown, options);
    this.addEventListener('mousemove', this.#handleMouseMove, options);
    this.addEventListener('mouseup', this.#handleMouseUp, options);
    this.addEventListener('mouseleave', this.#handleMouseUp, options);
    this.addEventListener('wheel', this.#handleWheel, options);
    this.addEventListener('dblclick', this.#handleDoubleClick, options);

    // Block click when zoomed/dragged
    this.addEventListener('click', (e) => {
      if (this.#hasDragged || this.#scale > 1.05) {
        e.stopPropagation();
        e.preventDefault();
      }
      this.#hasDragged = false;
    }, { capture: true, signal });

    this.#image?.addEventListener('load', this.#updateTransform, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
    this.#removeMinimap();
  }

  #handleResize = () => {
    if (this.#initialized) this.#requestUpdateTransform();
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  // ── TOUCH ──

  #handleTouchStart = (event) => {
    preventDefault(event);
    const now = Date.now();
    if (event.touches.length === 1 && now - this.#lastTapTime < 300) {
      this.#toggleZoom(touchToPoint(event.touches[0]));
      this.#lastTapTime = 0;
      return;
    }
    this.#lastTapTime = now;

    if (event.touches.length === 2) {
      const [p1, p2] = Array.from(event.touches).map(touchToPoint);
      if (p1 && p2) this.#startZoomGesture(p1, p2);
    } else if (event.touches.length === 1) {
      const p = touchToPoint(event.touches[0]);
      if (p) this.#startDragGesture(p);
    }
  };

  #handleTouchMove = (event) => {
    preventDefault(event);
    if (event.touches.length === 2) this.#processZoomGesture(event);
    else if (event.touches.length === 1 && this.#isDragging) this.#processDragGesture(event);
  };

  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      this.#isDragging = false;
      this.#requestUpdateTransform();
    }
  };

  // ── MOUSE ──

  #handleMouseDown = (event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    this.#isMouseDragging = true;
    this.#hasDragged = false;
    this.#startPosition = { x: event.clientX, y: event.clientY };
    this.#startTranslate = { ...this.#translate };
    this.style.cursor = 'grabbing';
  };

  #handleMouseMove = (event) => {
    if (!this.#isMouseDragging) return;
    event.preventDefault();
    const dx = event.clientX - this.#startPosition.x;
    const dy = event.clientY - this.#startPosition.y;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) this.#hasDragged = true;
    this.#translate = {
      x: this.#startTranslate.x + dx / this.#scale,
      y: this.#startTranslate.y + dy / this.#scale,
    };
    this.#requestUpdateTransform();
  };

  #handleMouseUp = () => {
    if (!this.#isMouseDragging) return;
    this.#isMouseDragging = false;
    this.style.cursor = this.#scale > 1.05 ? 'grab' : 'zoom-in';
  };

  #handleWheel = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = this.getBoundingClientRect();
    const cursorX = event.clientX - rect.left;
    const cursorY = event.clientY - rect.top;
    const oldScale = this.#scale;
    const delta = event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    this.#scale = clamp(this.#scale + delta, MIN_ZOOM, MAX_ZOOM);
    const center = { x: rect.width / 2, y: rect.height / 2 };
    const dist = { x: cursorX - center.x, y: cursorY - center.y };
    const scaleDelta = this.#scale / oldScale - 1.0;
    this.#translate.x -= (dist.x * scaleDelta) / this.#scale;
    this.#translate.y -= (dist.y * scaleDelta) / this.#scale;
    this.#requestUpdateTransform();
    this.style.cursor = this.#scale > 1.05 ? 'grab' : 'zoom-in';
  };

  #handleDoubleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#toggleZoom({ x: event.clientX, y: event.clientY });
  };

  #toggleZoom(point) {
    if (this.#scale > 1.05) {
      this.#scale = MIN_ZOOM;
      this.#translate = { x: 0, y: 0 };
    } else if (point) {
      const rect = this.getBoundingClientRect();
      const center = { x: rect.width / 2, y: rect.height / 2 };
      const dist = { x: point.x - rect.left - center.x, y: point.y - rect.top - center.y };
      this.#scale = DOUBLE_TAP_ZOOM;
      this.#translate.x = -(dist.x * (DOUBLE_TAP_ZOOM - 1)) / DOUBLE_TAP_ZOOM;
      this.#translate.y = -(dist.y * (DOUBLE_TAP_ZOOM - 1)) / DOUBLE_TAP_ZOOM;
    } else {
      this.#scale = DOUBLE_TAP_ZOOM;
    }
    this.#requestUpdateTransform();
    this.style.cursor = this.#scale > 1.05 ? 'grab' : 'zoom-in';
  }

  // ── GESTURES ──

  #startZoomGesture(p1, p2) {
    this.#initialDistance = getDistance(p1, p2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  #startDragGesture(point) {
    this.#startPosition = { ...point };
    this.#startTranslate = { ...this.#translate };
    this.#isDragging = true;
  }

  #processZoomGesture(event) {
    const [p1, p2] = Array.from(event.touches).map(touchToPoint);
    if (!p1 || !p2) return;
    const mid = getMidpoint(p1, p2);
    const dist = getDistance(p1, p2);
    const oldScale = this.#scale;
    this.#scale = clamp((dist / this.#initialDistance) * this.#startScale, MIN_ZOOM, MAX_ZOOM);
    const center = { x: this.clientWidth / 2, y: this.clientHeight / 2 };
    const fromCenter = { x: mid.x - center.x, y: mid.y - center.y };
    const scaleDelta = this.#scale / oldScale - 1.0;
    this.#translate.x -= (fromCenter.x * scaleDelta) / this.#scale;
    this.#translate.y -= (fromCenter.y * scaleDelta) / this.#scale;
    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  #processDragGesture(event) {
    const p = touchToPoint(event.touches[0]);
    if (!p) return;
    this.#translate = {
      x: this.#startTranslate.x + (p.x - this.#startPosition.x) / this.#scale,
      y: this.#startTranslate.y + (p.y - this.#startPosition.y) / this.#scale,
    };
    this.#requestUpdateTransform();
  }

  // ── MINIMAP ──

  #ensureMinimap() {
    if (!this.#image || this.#minimapEl) return;

    this.#minimapEl = document.createElement('div');
    this.#minimapEl.className = 'drag-zoom-minimap';
    Object.assign(this.#minimapEl.style, {
      position: 'absolute', bottom: `${MINIMAP_MARGIN}px`, right: `${MINIMAP_MARGIN}px`,
      width: `${MINIMAP_SIZE}px`, height: `${MINIMAP_SIZE}px`,
      border: '2px solid rgba(255,255,255,0.85)', borderRadius: '6px',
      overflow: 'hidden', zIndex: '10', pointerEvents: 'none',
      boxShadow: '0 2px 12px rgba(0,0,0,0.35)', backgroundColor: 'rgba(0,0,0,0.15)',
      transition: 'opacity 0.25s ease', opacity: '0',
    });

    this.#minimapCanvas = document.createElement('canvas');
    this.#minimapCanvas.width = MINIMAP_SIZE * 2;
    this.#minimapCanvas.height = MINIMAP_SIZE * 2;
    Object.assign(this.#minimapCanvas.style, { width: '100%', height: '100%', objectFit: 'contain' });
    this.#minimapEl.appendChild(this.#minimapCanvas);

    this.#minimapViewport = document.createElement('div');
    Object.assign(this.#minimapViewport.style, {
      position: 'absolute', border: '2px solid rgba(255,120,50,0.9)',
      backgroundColor: 'rgba(255,120,50,0.15)', borderRadius: '2px',
    });
    this.#minimapEl.appendChild(this.#minimapViewport);

    this.style.position = 'relative';
    this.appendChild(this.#minimapEl);
    this.#drawMinimapThumbnail();
  }

  #drawMinimapThumbnail() {
    if (!this.#minimapCanvas || !this.#image) return;
    const ctx = this.#minimapCanvas.getContext('2d');
    if (!ctx) return;
    const img = this.#image;
    if (!img.complete || !img.naturalWidth) {
      img.addEventListener('load', () => this.#drawMinimapThumbnail(), { once: true });
      return;
    }
    const cw = this.#minimapCanvas.width, ch = this.#minimapCanvas.height;
    const aspect = img.naturalWidth / img.naturalHeight;
    let dw, dh, dx, dy;
    if (aspect > 1) { dw = cw; dh = cw / aspect; dx = 0; dy = (ch - dh) / 2; }
    else { dh = ch; dw = ch * aspect; dx = (cw - dw) / 2; dy = 0; }
    ctx.clearRect(0, 0, cw, ch);
    ctx.drawImage(img, dx, dy, dw, dh);
  }

  #updateMinimap() {
    if (!this.#minimapEl || !this.#minimapViewport) return;
    const isZoomed = this.#scale > 1.05;
    this.#minimapEl.style.opacity = isZoomed ? '1' : '0';
    if (!isZoomed) return;

    const dims = this.#getImageDimensions();
    if (!dims) return;
    const { width: imgW, height: imgH } = dims;
    const cW = this.clientWidth, cH = this.clientHeight;
    const imgAspect = imgW / imgH;
    let mW, mH, mX, mY;
    if (imgAspect > 1) { mW = MINIMAP_SIZE; mH = MINIMAP_SIZE / imgAspect; mX = 0; mY = (MINIMAP_SIZE - mH) / 2; }
    else { mH = MINIMAP_SIZE; mW = MINIMAP_SIZE * imgAspect; mX = (MINIMAP_SIZE - mW) / 2; mY = 0; }

    const vpWR = Math.min(1, cW / (imgW * this.#scale));
    const vpHR = Math.min(1, cH / (imgH * this.#scale));
    const vpCXR = 0.5 - this.#translate.x / imgW;
    const vpCYR = 0.5 - this.#translate.y / imgH;

    Object.assign(this.#minimapViewport.style, {
      left: `${clamp(mX + (vpCXR - vpWR / 2) * mW, mX, mX + mW - 4)}px`,
      top: `${clamp(mY + (vpCYR - vpHR / 2) * mH, mY, mY + mH - 4)}px`,
      width: `${clamp(vpWR * mW, 4, mW)}px`,
      height: `${clamp(vpHR * mH, 4, mH)}px`,
    });
  }

  #removeMinimap() {
    this.#minimapEl?.parentNode?.removeChild(this.#minimapEl);
    this.#minimapEl = this.#minimapViewport = this.#minimapCanvas = null;
  }

  // ── TRANSFORM ──

  #getImageDimensions = () => {
    if (!this.#image) return null;
    const { naturalWidth: nw, naturalHeight: nh } = this.#image;
    const cw = this.clientWidth, ch = this.clientHeight;
    if (!nw || !nh || !cw || !ch) return null;
    const cAspect = cw / ch, nAspect = nw / nh;
    let w, h;
    if (nAspect > cAspect) { w = cw; h = cw / nAspect; }
    else { h = ch; w = ch * nAspect; }
    return (w && h) ? { width: w, height: h } : null;
  };

  #constrainTranslation() {
    const cw = this.clientWidth, ch = this.clientHeight;
    if (!cw || !ch) return;
    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);
    const dims = this.#getImageDimensions();
    if (!dims) return;
    const exW = Math.max(0, dims.width * this.#scale - cw);
    const exH = Math.max(0, dims.height * this.#scale - ch);
    const maxTx = exW / (2 * this.#scale);
    const maxTy = exH / (2 * this.#scale);
    this.#translate.x = clamp(this.#translate.x, -maxTx, maxTx);
    this.#translate.y = clamp(this.#translate.y, -maxTy, maxTy);
  }

  #requestUpdateTransform = () => {
    if (!this.#animationFrame) this.#animationFrame = requestAnimationFrame(this.#updateTransform);
  };

  #cancelAnimationFrame() {
    if (this.#animationFrame) { cancelAnimationFrame(this.#animationFrame); this.#animationFrame = null; }
  }

  #updateTransform = () => {
    this.#animationFrame = null;
    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
    this.#ensureMinimap();
    this.#updateMinimap();
  };

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
    this.#removeMinimap();
  }
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

function touchToPoint(touch) {
  return touch ? { x: touch.clientX, y: touch.clientY } : undefined;
}

function getDistance(p1, p2) {
  const dx = p1.x - p2.x, dy = p1.y - p2.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function getMidpoint(p1, p2) {
  return { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
}